/**
 * Грубий розбір CSV з Cost Explorer / CUR: шукає колонки з сумами та сервісами.
 * Повертає суму Unblended/Amount у USD, якщо вдається визначити.
 */
export type CostCsvParseResult = {
  totalUsd: number | null;
  /** Топ сервісів за сумою (назва → USD), якщо знайдено. */
  byService: Record<string, number>;
  /** Повідомлення для користувача (помилка / підказка). */
  hint: "ok" | "no_numbers" | "ambiguous";
};

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, "_");
}

function parseMoneyCell(raw: string): number | null {
  const s = raw.trim().replace(/,/g, "").replace(/^\$/, "");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function parseCostExplorerCsv(text: string): CostCsvParseResult {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    return { totalUsd: null, byService: {}, hint: "no_numbers" };
  }

  const delim = lines[0].includes("\t") ? "\t" : ",";
  const headers = lines[0].split(delim).map(normalizeHeader);

  let amountIdx = -1;
  let serviceIdx = -1;
  for (let i = 0; i < headers.length; i++) {
    const h = headers[i]!;
    if (
      amountIdx < 0 &&
      (h.includes("unblendedcost") ||
        h.includes("lineitem_unblendedcost") ||
        h === "cost" ||
        h.includes("amount") ||
        h === "total_cost" ||
        h.includes("blendedcost"))
    ) {
      amountIdx = i;
    }
    if (
      serviceIdx < 0 &&
      (h.includes("product_product_name") ||
        h.includes("product_name") ||
        h.includes("lineitem_productcode") ||
        h === "service" ||
        h.includes("servicename") ||
        h === "lineitem_lineitemtype")
    ) {
      serviceIdx = i;
    }
  }

  if (amountIdx < 0) {
    const nums: number[] = [];
    for (let r = 1; r < Math.min(lines.length, 500); r++) {
      const cells = lines[r]!.split(delim);
      for (const c of cells) {
        const n = parseMoneyCell(c);
        if (n !== null && Math.abs(n) < 1e12) nums.push(Math.abs(n));
      }
    }
    if (nums.length === 0) {
      return { totalUsd: null, byService: {}, hint: "no_numbers" };
    }
    const sum = nums.reduce((a, b) => a + b, 0);
    return { totalUsd: sum, byService: {}, hint: "ambiguous" };
  }

  const byService: Record<string, number> = {};
  let sum = 0;
  for (let r = 1; r < lines.length; r++) {
    const cells = lines[r]!.split(delim);
    const rawAmt = cells[amountIdx];
    if (rawAmt === undefined) continue;
    const n = parseMoneyCell(rawAmt);
    if (n === null) continue;
    sum += n;
    if (serviceIdx >= 0 && cells[serviceIdx]) {
      const name = cells[serviceIdx]!.trim() || "Other";
      byService[name] = (byService[name] ?? 0) + n;
    }
  }

  if (!Number.isFinite(sum) || sum === 0) {
    return { totalUsd: null, byService: {}, hint: "no_numbers" };
  }

  return {
    totalUsd: Math.round(sum * 100) / 100,
    byService,
    hint: Object.keys(byService).length ? "ok" : "ambiguous",
  };
}
